import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode
} from "react";

import type {
  SpecPolicyFlag,
  SpecPolicyFlagSet
} from "@automatry/schemas";
import type { WorkspaceMode } from "../app/types";
import { reloadForLazyRouteRecovery } from "../app/lazyRouteRecovery";
import { RetryableLazyRoute } from "../components/LazyRouteBoundary";
import type {
  ApartmentSolutionDisplayViewModel,
  ApartmentSolutionForDisplay,
  SpecMatrixDisplayViewModel
} from "../viewmodel/solutionViewAdapter";
import type {
  ProjectFixtureOutputBundle,
  ProjectFixtureQuotePricing
} from "../viewmodel/projectCockpitAdapter";
import { primaryHumanFindingLabel } from "../app/specifyFindings";
import {
  advisoryFindingsForApartmentType,
  type ProjectFixtureAdvisories,
  type ProjectFixtureTerminalFinding
} from "../app/projectData";
import type {
  ProjectFixtureDraftScope,
  ProjectFixtureDraftScopeType
} from "../viewmodel/projectCockpitAdapter";
import { cx, pointTone } from "./shared";
import { Term } from "./Glossary";
import type { SpecTabProps } from "./spec/specTabProps";
import type { DownstreamConfidenceModel } from "../viewmodel/downstreamConfidenceTypes";

/**
 * The Specification Results tabs (design `tabDef`, Titan-Controls-Specifier.dc.html
 * line ~1567). `SpecWorkspace` owns the active-tab state; the tab bodies live in
 * `./spec/*` and consume the pinned `SpecTabProps` contract.
 */
export type SpecTab = "conn" | "points" | "prod" | "floor" | "panel" | "schedules";

export const SPEC_TABS: readonly { id: SpecTab; label: string }[] = [
  { id: "conn", label: "Connections" },
  { id: "points", label: "Points & ports" },
  { id: "prod", label: "Products" },
  { id: "floor", label: "Floor plan" },
  { id: "panel", label: "Panel make-up" },
  { id: "schedules", label: "Schedules" }
];

const loadConnectionsTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./spec/ConnectionsTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./spec/ConnectionsTab"))
      : await import("./spec/ConnectionsTab");

  return { default: module.default as ComponentType<SpecTabProps> };
};

const loadPointsPortsTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./spec/PointsPortsTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./spec/PointsPortsTab"))
      : await import("./spec/PointsPortsTab");

  return { default: module.default as ComponentType<SpecTabProps> };
};

const loadProductsTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./spec/ProductsTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./spec/ProductsTab"))
      : await import("./spec/ProductsTab");

  return {
    default: module.default as ComponentType<SpecTabProps>
  };
};

const loadFloorPlanTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./spec/FloorPlanTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./spec/FloorPlanTab"))
      : await import("./spec/FloorPlanTab");

  return {
    default: module.default as ComponentType<SpecTabProps>
  };
};

const loadPanelMakeupTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./spec/PanelMakeupTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./spec/PanelMakeupTab"))
      : await import("./spec/PanelMakeupTab");

  return {
    default: module.default as ComponentType<SpecTabProps>
  };
};

const loadSchedulesTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./spec/SchedulesTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./spec/SchedulesTab"))
      : await import("./spec/SchedulesTab");

  return {
    default: module.default as ComponentType<SpecTabProps>
  };
};

const ProductsRouteLoadingScreen = lazy(async () => {
  const module = await import("./spec/ProductsRouteLoadingScreen");

  return {
    default: module.ProductsRouteLoadingScreen as ComponentType<{
      view: ApartmentSolutionDisplayViewModel;
      showQuotePricingNotice?: boolean;
    }>
  };
});

function ProductsTabLoadingFallback({
  view,
  showQuotePricingNotice
}: {
  view: ApartmentSolutionDisplayViewModel;
  showQuotePricingNotice: boolean;
}) {
  return (
    <Suspense fallback={<ProductsTabLoadingRail />}>
      <ProductsRouteLoadingScreen
        view={view}
        showQuotePricingNotice={showQuotePricingNotice}
      />
    </Suspense>
  );
}

function ProductsTabLoadingRail() {
  return (
    <section aria-busy="true" className="max-w-[760px]">
      <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
        Products
      </div>
      <h2 className="m-0 mt-1 text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        Loading product schedule...
      </h2>
      <div
        role="progressbar"
        aria-label="Loading products tab"
        className="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span className="block h-full w-1/3 rounded-full bg-[#A87518] animate-tcsLoading" />
      </div>
    </section>
  );
}

function FloorPlanTabLoadingFallback() {
  return (
    <section aria-busy="true" className="max-w-[760px]">
      <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
        Floor plan
      </div>
      <h2 className="m-0 mt-1 text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        Loading floor plan...
      </h2>
      <div
        role="progressbar"
        aria-label="Loading floor plan tab"
        className="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span className="block h-full w-1/3 rounded-full bg-[#32745E] animate-tcsLoading" />
      </div>
    </section>
  );
}

function PanelMakeupTabLoadingFallback() {
  return (
    <section aria-busy="true" className="max-w-[760px]">
      <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
        Panel make-up
      </div>
      <h2 className="m-0 mt-1 text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        Loading panel make-up...
      </h2>
      <div
        role="progressbar"
        aria-label="Loading panel make-up tab"
        className="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span className="block h-full w-1/3 rounded-full bg-[#6A5E97] animate-tcsLoading" />
      </div>
    </section>
  );
}

function SchedulesTabLoadingFallback() {
  return (
    <section aria-busy="true" className="max-w-[760px]">
      <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
        Schedules
      </div>
      <h2 className="m-0 mt-1 text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        Loading client schedules...
      </h2>
      <div
        role="progressbar"
        aria-label="Loading schedules tab"
        className="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span className="block h-full w-1/3 rounded-full bg-[#A84F36] animate-tcsLoading" />
      </div>
    </section>
  );
}

function ControlsTabLoadingFallback({
  label,
  accent
}: {
  label: string;
  accent: string;
}) {
  return (
    <section
      aria-busy="true"
      className="max-w-[760px]"
      data-testid="controls-tab-loading"
    >
      <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
        {label}
      </div>
      <h2 className="m-0 mt-1 text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        Loading {label.toLocaleLowerCase()}...
      </h2>
      <div
        role="progressbar"
        aria-label={`Loading ${label.toLocaleLowerCase()} tab`}
        className="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span
          className="block h-full w-1/3 animate-tcsLoading rounded-full"
          style={{ backgroundColor: accent }}
        />
      </div>
    </section>
  );
}

export function SpecWorkspace({
  view,
  solution,
  outputBundle,
  quotePricing,
  advisories,
  partialScope,
  draftScope,
  projectId,
  onOpenItem,
  onOpenQuote,
  requirementsStale = false,
  onRerunSpecification,
  apartmentTypes,
  scheduleEntries = [],
  onScheduleEntriesChanged,
  downstreamConfidence,
  policyFlagSet,
  initialTab = "conn",
  activeTab,
  onTab,
  disableRepeatedFailureReload = false
}: {
  // `mode` / `matrix` / `onMode` are still passed by router.tsx (sibling-owned) but the
  // design has no in-page Simple/Advanced toggle, so they are accepted-but-unused here:
  // Take-offs is routed as its own screen (see SPEC-PAGE-PLAN T5/T6).
  mode?: WorkspaceMode;
  view: ApartmentSolutionDisplayViewModel;
  // The raw solution `view` was built from — forwarded to the active tab so it can
  // surface per-device room labels / UFH chips. Optional: tabs degrade gracefully.
  solution?: ApartmentSolutionForDisplay;
  outputBundle?: ProjectFixtureOutputBundle;
  quotePricing?: ProjectFixtureQuotePricing | null;
  advisories?: ProjectFixtureAdvisories | null;
  partialScope?: {
    message: string;
    completedApartmentTypeIds: readonly string[];
    omittedApartmentTypeIds: readonly string[];
  } | null;
  /**
   * Track 1: present when the run produced at least one NOT-CERTIFIED draft
   * apartment type. Drives the unmissable review panel and the per-type
   * certification chip. Never present for a fully certified run.
   */
  draftScope?: ProjectFixtureDraftScope | null;
  projectId?: string;
  matrix?: SpecMatrixDisplayViewModel;
  onMode?: (mode: WorkspaceMode) => void;
  onOpenItem: (id: string) => void;
  onOpenQuote?: () => void;
  requirementsStale?: boolean | undefined;
  onRerunSpecification?: (() => void) | undefined;
  apartmentTypes?: SpecTabProps["apartmentTypes"];
  scheduleEntries?: SpecTabProps["scheduleEntries"];
  onScheduleEntriesChanged?: SpecTabProps["onScheduleEntriesChanged"];
  downstreamConfidence?: DownstreamConfidenceModel;
  // Project-level policy-flag decisions (control topology, metering, etc.). Optional:
  // the band below is omitted entirely when absent, matching the honest-empty-state
  // pattern used by the advisory/draft bands above it.
  policyFlagSet?: SpecPolicyFlagSet | null | undefined;
  initialTab?: SpecTab;
  activeTab?: SpecTab;
  onTab?: (tab: SpecTab) => void;
  disableRepeatedFailureReload?: boolean | undefined;
}) {
  const [localTab, setLocalTab] = useState<SpecTab>(initialTab);
  const tab = activeTab ?? localTab;
  const [animateTabPanel, setAnimateTabPanel] = useState(false);
  const tabActivationRef = useRef<"keyboard" | "pointer">("keyboard");
  const [visitedTabs, setVisitedTabs] = useState<Set<SpecTab>>(
    () => new Set([activeTab ?? initialTab])
  );
  const rerunSpecification = () => {
    if (
      !onRerunSpecification ||
      !globalThis.confirm("This will re-run Specify and replace its results")
    ) {
      return;
    }

    onRerunSpecification();
  };
  const advisoryFindings = advisoryFindingsForApartmentType(
    advisories,
    view.apartmentTypeId
  );
  const draftType = draftScope?.types.find(
    (type) => type.apartmentTypeId === view.apartmentTypeId
  );
  const isDraftType = draftType !== undefined;
  // Per-type certification: a verified sibling in a mixed project must stay
  // visibly verified, so certification is asserted per apartment type and
  // never inferred from the project-level presence of a draft.
  const certificationLabel = isDraftType
    ? "Draft — not certified"
    : draftScope
      ? "Verified"
      : undefined;
  const tabProps: SpecTabProps = {
    view,
    onOpenItem,
    ...(solution ? { solution } : {}),
    ...(outputBundle ? { outputBundle } : {}),
    ...(quotePricing !== undefined ? { quotePricing } : {}),
    ...(projectId ? { projectId } : {}),
    ...(apartmentTypes ? { apartmentTypes } : {}),
    ...(onOpenQuote ? { onOpenQuote } : {}),
    scheduleEntries,
    ...(onScheduleEntriesChanged ? { onScheduleEntriesChanged } : {}),
    ...(downstreamConfidence ? { downstreamConfidence } : {})
  };

  useEffect(() => {
    setVisitedTabs((current) => {
      if (current.has(tab)) {
        return current;
      }

      const next = new Set(current);
      next.add(tab);
      return next;
    });
  }, [tab]);

  const handleTabSelect = (nextTab: SpecTab, shouldAnimate: boolean) => {
    if (nextTab === tab) {
      return;
    }

    setVisitedTabs((current) => {
      if (current.has(nextTab)) {
        return current;
      }

      const next = new Set(current);
      next.add(nextTab);
      return next;
    });
    setAnimateTabPanel(shouldAnimate);
    if (onTab) {
      onTab(nextTab);
      return;
    }

    setLocalTab(nextTab);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="bg-[var(--bg)] px-5 pt-3 sm:px-7 lg:px-9 lg:pt-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
              Apartment type
            </div>
            <h1 className="m-0 mt-0.5 text-[22px] font-extrabold tracking-[-.03em] sm:text-[24px]">
              {view.title}{" "}
              <span className="font-semibold text-[var(--faint)]">
                · {isDraftType ? "draft" : "generated"}
              </span>
            </h1>
            {certificationLabel ? (
              <span
                data-testid="spec-certification-status"
                data-certification-status={
                  isDraftType ? "not_certified" : "certified"
                }
                className={cx(
                  "mt-1.5 inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[.06em]",
                  isDraftType
                    ? "border-[#D79A6A] bg-[#FBF0E7] text-[#8A4B18]"
                    : "border-[#B6DCC5] bg-[#EEF7F1] text-[#1F5A3A]"
                )}
              >
                {certificationLabel}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:flex-none lg:justify-end">
            {advisoryFindings.length > 0 ? (
              <span className="inline-flex items-center rounded-full border border-[#E7D09A] bg-[#FBF7EF] px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[.06em] text-[#7B5818]">
                {advisoryCountLabel(advisoryFindings.length)}
              </span>
            ) : null}
            <div className="min-w-[128px] lg:text-right">
              <div className="text-[11px] font-bold text-[var(--faint)]">
                x{view.apartmentQuantity} of this type
              </div>
              <div className="mt-0.5 text-[11px] text-[var(--faint)]">
                {isDraftType
                  ? "best-effort draft from the project inputs"
                  : partialScope
                    ? "from the verified partial workspace"
                    : "based on the project inputs"}
              </div>
            </div>
          </div>
        </div>
        {partialScope ? (
          <div className="mt-4 rounded-[8px] border border-[#E7D09A] bg-[#FBF7EF] px-3 py-2 text-[12px] font-bold leading-[1.35] text-[#7B5818]">
            {partialScope.message}
          </div>
        ) : null}
        {draftScope ? (
          <DraftReviewPanel
            draftScope={draftScope}
            {...(draftType ? { draftType } : {})}
            apartmentTypeTitle={view.title}
          />
        ) : null}
        {requirementsStale ? (
          <div
            role="status"
            className="mt-4 flex flex-col gap-3 rounded-[8px] border border-[#D7B46A] bg-[#FFF7E3] px-3 py-3 text-[#6F4D12] sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="m-0 text-[12.5px] font-extrabold leading-[1.35]">
              Requirements have changed since this specification was generated.
            </p>
            {onRerunSpecification ? (
              <button
                type="button"
                onClick={rerunSpecification}
                className="w-full cursor-pointer rounded-[8px] bg-[#7B5818] px-3 py-2 text-[12px] font-extrabold text-white hover:bg-[#684A14] sm:w-auto"
              >
                Re-run stage
              </button>
            ) : null}
          </div>
        ) : null}
        {!requirementsStale && onRerunSpecification ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[8px] border border-[var(--line)] bg-[var(--raised2)] px-3 py-2.5">
            <p className="m-0 text-[12px] font-semibold text-[var(--muted)]">
              This completed specification is stored and stays unchanged while you
              review it.
            </p>
            <button
              type="button"
              data-testid="rerun-specify-stage"
              onClick={rerunSpecification}
              className="rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-3 py-2 text-[12px] font-extrabold text-[var(--ink2)] hover:bg-[var(--chip)]"
            >
              Re-run stage
            </button>
          </div>
        ) : null}
        {advisoryFindings.length > 0 ? (
          <AdvisoryReviewBand findings={advisoryFindings} />
        ) : null}
        {policyFlagSet && policyFlagSet.activeFlags.length > 0 ? (
          <PolicyFlagsBand activeFlags={policyFlagSet.activeFlags} />
        ) : null}
        <TakeoffStats view={view} />
        <div
          role="tablist"
          aria-label="Specification result views"
          data-showme="specification-views"
          className="mt-3 flex gap-0.5 overflow-x-auto border-b border-[var(--line)]"
        >
          {SPEC_TABS.map(({ id, label }) => {
            const active = id === tab;

            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`spec-tab-${id}`}
                aria-controls={`spec-panel-${id}`}
                aria-selected={active}
                onPointerDown={() => {
                  tabActivationRef.current = "pointer";
                }}
                onClick={(event) => {
                  // Pointer tab changes get surface motion; keyboard activation stays
                  // instant so focus navigation does not feel noisy.
                  handleTabSelect(
                    id,
                    tabActivationRef.current === "pointer" || event.detail > 0
                  );
                  tabActivationRef.current = "keyboard";
                }}
                className={cx(
                  "mr-[22px] min-h-10 cursor-pointer whitespace-nowrap border-b-2 px-1 py-2 text-[13px] font-bold transition-colors",
                  active
                    ? "mb-[-1px] border-[var(--ink)] text-[var(--ink)]"
                    : "border-transparent text-[var(--faint)] hover:text-[var(--muted)]"
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </header>
      <div
        role="tabpanel"
        data-active-spec-tab={tab}
        className="flex-1 overflow-auto px-5 py-4 pb-[60px] sm:px-7 lg:px-9 lg:py-5"
      >
        {/* Keep visited panels mounted while hidden so tab-owned state survives and
            already measured shells stay warm between switches. */}
        <SpecWorkspacePanel
          id="conn"
          active={tab === "conn"}
          visited={visitedTabs.has("conn")}
          animate={animateTabPanel}
        >
          <RetryableLazyRoute
            load={loadConnectionsTab}
            surfaceName="Connections"
            fallback={
              <ControlsTabLoadingFallback label="Connections" accent="#4F6B8F" />
            }
            render={(ConnectionsTab) => <ConnectionsTab {...tabProps} />}
          />
        </SpecWorkspacePanel>
        <SpecWorkspacePanel
          id="points"
          active={tab === "points"}
          visited={visitedTabs.has("points")}
          animate={animateTabPanel}
        >
          <RetryableLazyRoute
            load={loadPointsPortsTab}
            surfaceName="Points & ports"
            fallback={
              <ControlsTabLoadingFallback label="Points & ports" accent="#6E8A4C" />
            }
            render={(PointsPortsTab) => <PointsPortsTab {...tabProps} />}
          />
        </SpecWorkspacePanel>
        <SpecWorkspacePanel
          id="prod"
          active={tab === "prod"}
          visited={visitedTabs.has("prod")}
          animate={animateTabPanel}
        >
          <RetryableLazyRoute
            load={loadProductsTab}
            surfaceName="Products"
            onRepeatedFailureReload={
              projectId && !disableRepeatedFailureReload
                ? () =>
                    reloadForLazyRouteRecovery({
                      projectId,
                      screen: "spec",
                      apartmentTypeId: view.apartmentTypeId,
                      specTab: "prod"
                    })
                : undefined
            }
            fallback={
              <ProductsTabLoadingFallback
                view={view}
                showQuotePricingNotice={Boolean(outputBundle && quotePricing)}
              />
            }
            render={(ProductsTab) => <ProductsTab {...tabProps} />}
          />
        </SpecWorkspacePanel>
        <SpecWorkspacePanel
          id="floor"
          active={tab === "floor"}
          visited={visitedTabs.has("floor")}
          animate={animateTabPanel}
        >
          <RetryableLazyRoute
            load={loadFloorPlanTab}
            surfaceName="Floor plan"
            fallback={<FloorPlanTabLoadingFallback />}
            render={(FloorPlanTab) => <FloorPlanTab {...tabProps} />}
          />
        </SpecWorkspacePanel>
        <SpecWorkspacePanel
          id="panel"
          active={tab === "panel"}
          visited={visitedTabs.has("panel")}
          animate={animateTabPanel}
        >
          <RetryableLazyRoute
            load={loadPanelMakeupTab}
            surfaceName="Panel make-up"
            fallback={<PanelMakeupTabLoadingFallback />}
            render={(PanelMakeupTab) => <PanelMakeupTab {...tabProps} />}
          />
        </SpecWorkspacePanel>
        <SpecWorkspacePanel
          id="schedules"
          active={tab === "schedules"}
          visited={visitedTabs.has("schedules")}
          animate={animateTabPanel}
        >
          <RetryableLazyRoute
            load={loadSchedulesTab}
            surfaceName="Schedules"
            onRepeatedFailureReload={
              projectId && !disableRepeatedFailureReload
                ? () =>
                    reloadForLazyRouteRecovery({
                      projectId,
                      screen: "spec",
                      apartmentTypeId: view.apartmentTypeId,
                      specTab: "schedules"
                    })
                : undefined
            }
            fallback={<SchedulesTabLoadingFallback />}
            render={(SchedulesTab) => <SchedulesTab {...tabProps} />}
          />
        </SpecWorkspacePanel>
      </div>
    </div>
  );
}

function SpecWorkspacePanel({
  id,
  active,
  visited,
  animate,
  children
}: {
  id: SpecTab;
  active: boolean;
  visited: boolean;
  animate: boolean;
  children: ReactNode;
}) {
  return (
    <section
      id={`spec-panel-${id}`}
      aria-labelledby={`spec-tab-${id}`}
      hidden={!active}
      className={cx(
        "tcsTabPanelShell min-h-[760px]",
        active ? "block" : "hidden",
        active && animate && "tcsTabPanelEnter"
      )}
    >
      {visited ? children : null}
    </section>
  );
}

const advisoryCountLabel = (count: number): string =>
  `${count} ${count === 1 ? "advisory" : "advisories"}`;

/**
 * Track 1 P0.4: the unmissable "Draft — not certified — needs review" panel.
 *
 * It states plainly what the user is looking at, lists the unresolved checks
 * in the same human language the run screen uses, and says which actions are
 * blocked. It renders on every apartment type of a project containing a draft
 * so a verified sibling still tells the user the project as a whole is not
 * issuable — but the per-type chip above keeps that sibling visibly verified.
 */
function DraftReviewPanel({
  draftScope,
  draftType,
  apartmentTypeTitle
}: {
  draftScope: ProjectFixtureDraftScope;
  draftType?: ProjectFixtureDraftScopeType;
  apartmentTypeTitle: string;
}) {
  const isDraftType = draftType !== undefined;
  const findings = draftType?.unresolvedFindings ?? [];

  return (
    <section
      role="alert"
      data-testid="spec-draft-review-panel"
      aria-label="Draft — not certified — needs review"
      className="mt-4 rounded-[8px] border-2 border-[#D79A6A] bg-[#FBF0E7] px-3 py-3 text-[#8A4B18]"
    >
      <div className="text-[12px] font-extrabold uppercase tracking-[.08em]">
        Draft — not certified — needs review
      </div>
      <p className="m-0 mt-1.5 text-[12.5px] font-bold leading-[1.4]">
        {isDraftType
          ? `A best-effort specification and take-off were generated for ${apartmentTypeTitle}; the checks below remain unresolved.`
          : `${apartmentTypeTitle} is verified, but this project contains ${draftScope.includedDraftApartmentTypeIds.length.toString()} draft apartment type(s) that still need review.`}
      </p>
      {findings.length > 0 ? (
        <ul className="mt-2 grid gap-1 text-[12px] font-semibold leading-[1.35]">
          {findings.map((finding) => (
            <li key={`${finding.code}:${finding.message}`}>
              {primaryHumanFindingLabel(finding)}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="m-0 mt-2 text-[12px] font-bold leading-[1.4]">
        You can review, edit and download this draft and its take-off. Issuing,
        exporting, readiness and certification stay blocked until the listed
        checks are resolved and the specification is re-run.
      </p>
    </section>
  );
}

function AdvisoryReviewBand({
  findings
}: {
  findings: readonly ProjectFixtureTerminalFinding[];
}) {
  return (
    <section
      aria-label="Specification advisories"
      className="mt-4 rounded-[8px] border border-[#E7D09A] bg-[#FBF7EF] px-3 py-2.5 text-[#7B5818]"
    >
      <div className="text-[11px] font-extrabold uppercase tracking-[.08em]">
        Completed with {advisoryCountLabel(findings.length)}
      </div>
      <ul className="mt-1.5 grid gap-1 text-[12px] font-semibold leading-[1.35]">
        {findings.slice(0, 4).map((finding) => (
          <li key={`${finding.code}:${finding.message}`}>
            {primaryHumanFindingLabel(finding)}
          </li>
        ))}
      </ul>
    </section>
  );
}

const policyFlagValueLabel = (value: SpecPolicyFlag["value"]): string => {
  const record = value as Record<string, unknown>;
  const kind = typeof record.kind === "string" ? record.kind : undefined;

  if (kind === "boolean") {
    return record.value === true ? "Yes" : "No";
  }
  if (kind === "mode" && typeof record.mode === "string") {
    return record.mode.replace(/_/g, " ");
  }
  if (kind === "integer" && typeof record.value === "number") {
    return String(record.value);
  }
  if (
    kind === "range" &&
    (typeof record.min === "number" || typeof record.max === "number")
  ) {
    return `${String(record.min ?? "—")}–${String(record.max ?? "—")}`;
  }

  const parts = Object.entries(record)
    .filter(([key]) => key !== "kind")
    .map(([key, entryValue]) =>
      typeof entryValue === "object" && entryValue !== null
        ? `${key}: ${JSON.stringify(entryValue)}`
        : `${key}: ${String(entryValue)}`
    );

  return parts.length > 0 ? parts.join(" · ") : (kind ?? "set");
};

const policyFlagConfidenceTone: Record<string, string> = {
  high: "text-[#1F5A3A]",
  medium: "text-[#7B5818]",
  low: "text-[#8A4B18]"
};

/**
 * Read-only summary of the AI's controls policy decisions (F2 contract:
 * `SpecPolicyFlagSet.activeFlags`) — the same 30-family set the create-flow's
 * `PolicyFlagsStep` lets a user answer during onboarding. Once a project is
 * specified there is no other live surface that shows these decisions, so
 * this band gives them visible, honest traceability (WHAT was decided, WHY)
 * without re-opening onboarding or offering an edit control that would not
 * persist here.
 */
function PolicyFlagsBand({
  activeFlags
}: {
  activeFlags: readonly SpecPolicyFlag[];
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleFlags = expanded ? activeFlags : activeFlags.slice(0, 6);

  return (
    <section
      aria-label="Controls policy flags"
      data-testid="spec-policy-flags-band"
      className="mt-4 rounded-[8px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]">
          {activeFlags.length} controls policy flag
          {activeFlags.length === 1 ? "" : "s"} applied
        </div>
        {activeFlags.length > 6 ? (
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className="cursor-pointer text-[11px] font-extrabold text-[var(--muted)] hover:text-[var(--ink)]"
          >
            {expanded ? "Show fewer" : `Show all ${activeFlags.length}`}
          </button>
        ) : null}
      </div>
      <ul className="mt-1.5 grid gap-1 text-[12px] font-semibold leading-[1.35] sm:grid-cols-2">
        {visibleFlags.map((flag) => (
          <li
            key={flag.id}
            className="flex items-baseline justify-between gap-2 rounded-md px-1.5 py-1"
            title={flag.why}
          >
            <span className="min-w-0 truncate text-[var(--ink2)]">
              {flag.humanName}
            </span>
            <span
              className={cx(
                "flex-none text-[11px] font-bold",
                policyFlagConfidenceTone[flag.confidence.label] ??
                  "text-[var(--muted)]"
              )}
            >
              {policyFlagValueLabel(flag.value)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TakeoffStats({ view }: { view: ApartmentSolutionDisplayViewModel }) {
  return (
    <div className="mt-3">
      <div className="mb-1.5 text-[9.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
        Property <Term name="take-off">take-offs</Term> · what&apos;s in this type
      </div>
      <div className="flex flex-wrap gap-1.5">
        {view.takeoffs.map((stat) => (
          <div
            key={stat.key}
            className="min-w-[92px] rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5"
          >
            <div className="flex items-baseline gap-[7px]">
              <span className="text-[17px] font-extrabold tracking-[-.03em] tabular-nums">
                {stat.value}
              </span>
              <span
                className="h-2 w-2 rounded-sm"
                style={{ background: pointTone[stat.tone] }}
              />
            </div>
            <div className="mt-px text-[10.5px] font-semibold text-[var(--muted2)]">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
